import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Globe, Search, Plus, ExternalLink, MoreHorizontal, SlidersHorizontal, Download, FolderPlus, Tag, Filter, X } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, StatusPill, EmptyState, Pagination, FilterBuilder, type ColumnDef, type FilterField, type FilterCondition } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Badge,
  Skeleton,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useDomainListPaged, useDeleteDomain, useDomainTags, type CDNDomain } from '@/api/manticore';
import { useServiceCdnDomainsPaged } from '@/api/manticore/service-cdn';
import { useMyProjects } from '@/api/projects';
import { useMyTeams } from '@/api/teams';
import {
  domainStatusTone,
  domainStatusLabel,
  isDomainDeletable,
  formatDate,
  useDebounce,
  DOMAIN_STATUS_LABEL,
} from './shared';
import { CdnConsoleBreadcrumbs } from './cdn/CdnConsoleBreadcrumbs';
import { DomainAssignDialog } from './DomainAssignDialog';
import { DomainTagsDialog } from './DomainTagsDialog';
import { DomainMoveDialog } from './DomainMoveDialog';
import styles from './DomainList.module.css';

const PAGE_SIZE = 50;

// Columns the user can toggle via "Display options" (domain + actions are fixed).
const TOGGLEABLE = [
  { id: 'origin', label: 'Origin' },
  { id: 'provider', label: 'Provider' },
  { id: 'tags', label: 'Tags' },
  { id: 'status', label: 'Status' },
  { id: 'created', label: 'Created' },
  { id: 'updated', label: 'Updated' },
];

// Origin is the core routing field for a CDN domains page — show by default.
const DEFAULT_VISIBLE: Record<string, boolean> = { origin: true, provider: false, tags: true, status: true, created: true, updated: false };

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;

/** Download the given domains as a CSV file. */
function exportCsv(rows: CDNDomain[], filename: string) {
  const head = ['domain', 'origin', 'provider', 'tags', 'status', 'created_at'];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const cell = (d: CDNDomain, k: string) => (k === 'tags' ? (d.tags ?? []).join('|') : d[k]);
  const body = rows.map((d) => head.map((k) => esc(cell(d, k))).join(',')).join('\n');
  const blob = new Blob([`${head.join(',')}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function DomainList() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId?: string }>();
  const isScoped = !!serviceId;
  // Carry the service context into the setup wizard so the new domain binds
  // to THIS service family's core provider instead of the org-wide default.
  const addDomainPath = isScoped
    ? `/services/cdn/${serviceId}/domains/new`
    : '/services/domains/new';
  // From the scoped console, detail stays nested under the service so the URL
  // keeps the console context (breadcrumb + back to this service's domains).
  // The org-wide entry links to the shared top-level detail route, since a
  // domain can move between services and isn't owned by one.
  const detailHref = (id: string | number | undefined) =>
    isScoped ? `/services/cdn/${serviceId}/domains/${id}` : `/services/domains/${id}`;
  const { show } = useToast();

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 400);
  const [page, setPage] = useState(1); // 1-based
  const [pendingDelete, setPendingDelete] = useState<CDNDomain | null>(null);
  const [visible, setVisible] = useState<Record<string, boolean>>(DEFAULT_VISIBLE);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState<CDNDomain | null>(null);

  const { data: projects = [] } = useMyProjects();
  const { data: teams = [] } = useMyTeams();
  const { data: tagOptions = [] } = useDomainTags();

  // Active scope is derived from the FilterBuilder conditions. Values are
  // collected across every row of a field (multiselect values are comma-joined).
  const collect = (field: string) =>
    conditions
      .filter((c) => c.field === field)
      .flatMap((c) => (c.value || '').split(','))
      .map((s) => s.trim())
      .filter(Boolean);
  const projectFilter = collect('project')[0];
  const teamFilter = collect('team')[0];
  const tagFilters = Array.from(new Set(collect('tag')));
  const statusFilters = Array.from(new Set(collect('status')));

  const filterFields: FilterField[] = [
    { value: 'status', label: 'Status', input: 'multiselect', operators: [{ value: 'is_any_of', label: 'is any of' }], options: Object.entries(DOMAIN_STATUS_LABEL).map(([v, l]) => ({ value: v, label: l })) },
    { value: 'tag', label: 'Tag', input: 'multiselect', operators: [{ value: 'is_any_of', label: 'is any of' }], options: [{ value: '__untagged__', label: 'Untagged' }, ...tagOptions.map((t) => ({ value: t, label: t }))] },
    { value: 'project', label: 'Project', input: 'select', operators: [{ value: 'equals', label: 'is' }], options: projects.map((p) => ({ value: p.id, label: p.name })) },
    { value: 'team', label: 'Team', input: 'select', operators: [{ value: 'equals', label: 'is' }], options: teams.map((t) => ({ value: t.id, label: t.team_name })) },
    { value: 'created', label: 'Created', input: 'date' },
  ];

  // Created date bounds from the date condition (after | before | between).
  const createdCond = conditions.find((c) => c.field === 'created' && c.value.trim());
  let createdFrom: string | undefined;
  let createdTo: string | undefined;
  if (createdCond) {
    const [a = '', b = ''] = createdCond.value.split(',');
    if (createdCond.operator === 'after') createdFrom = a || undefined;
    else if (createdCond.operator === 'before') createdTo = a || undefined;
    else if (createdCond.operator === 'between') {
      createdFrom = a || undefined;
      createdTo = b || undefined;
    }
  }

  // Build the shared query params object used by both hooks.
  const domainQuery = {
    page,
    perPage: PAGE_SIZE,
    search: debouncedSearch.trim() || undefined,
    // Sort is server-driven and fixed — the DataTable does not expose controlled
    // sorting props so client-side header clicks would only reorder the current
    // page (misleading on a paginated list). Sorting indicators are disabled on
    // all columns to avoid the false affordance.
    sort: 'domain',
    order: 'asc' as const,
    projectId: projectFilter || undefined,
    teamId: teamFilter || undefined,
    tags: tagFilters.length ? tagFilters : undefined,
    statuses: statusFilters.length ? statusFilters : undefined,
    createdFrom,
    createdTo,
  };

  // Server-side search + pagination — the list holds only the current page so
  // search reaches all 2000+ domains (ported from the legacy DomainList).
  // When serviceId is present, scope the query to that service's endpoint.
  const orgQ = useDomainListPaged({ ...domainQuery, enabled: !isScoped });
  const svcQ = useServiceCdnDomainsPaged(serviceId ?? '', { ...domainQuery, enabled: isScoped });
  const { data, isLoading, isFetching, isError, refetch } = isScoped ? svcQ : orgQ;

  const total = data?.pagination?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const remove = useDeleteDomain({
    onSuccess: () => {
      show({ tone: 'success', title: 'Domain deleted' });
      setPendingDelete(null);
    },
    onError: () => show({ tone: 'danger', title: 'Could not delete domain' }),
  });

  const rows = useMemo<CDNDomain[]>(
    () =>
      (data?.data ?? []).map((d, idx) => ({
        ...d,
        id: (d.id as string) || d.domain || String(idx),
        origin: d.origin || '—',
        status: d.status || 'pending',
      })),
    [data],
  );

  const selectedRows = useMemo(() => rows.filter((r) => rowSelection[String(r.id)]), [rows, rowSelection]);
  const selectedCount = selectedRows.length;

  const columns = useMemo<ColumnDef<CDNDomain, unknown>[]>(() => {
    const all: Record<string, ColumnDef<CDNDomain, unknown>> = {
      // Sorting disabled on all columns: the list is server-paginated and the
      // DataTable has no controlled-sort API, so header clicks would only reorder
      // the current page — a misleading affordance for a 2000+ domain list.
      origin: { id: 'origin', header: 'Origin', enableSorting: false, cell: ({ row }) => muted(String(row.original.origin ?? '—')) },
      provider: { id: 'provider', header: 'Provider', enableSorting: false, cell: ({ row }) => muted(String(row.original.provider ?? '—')) },
      tags: {
        id: 'tags',
        header: 'Tags',
        enableSorting: false,
        cell: ({ row }) => {
          const t = row.original.tags ?? [];
          return t.length === 0 ? (
            muted('—')
          ) : (
            <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
              {t.slice(0, 4).map((tag) => (
                <Badge key={tag} tone="neutral" appearance="subtle" size="sm">{tag}</Badge>
              ))}
              {t.length > 4 ? muted(`+${t.length - 4}`) : null}
            </span>
          );
        },
      },
      status: {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => <StatusPill tone={domainStatusTone(row.original.status)}>{domainStatusLabel(row.original.status)}</StatusPill>,
      },
      created: { id: 'created', header: 'Created', accessorKey: 'created_at', enableSorting: false, cell: ({ row }) => muted(formatDate(row.original.created_at)) },
      updated: { id: 'updated', header: 'Updated', accessorKey: 'updated_at', enableSorting: false, cell: ({ row }) => muted(formatDate(row.original.updated_at)) },
    };
    const domainCol: ColumnDef<CDNDomain, unknown> = {
      id: 'domain',
      accessorKey: 'domain',
      header: 'Domain',
      enableSorting: false,
      cell: ({ row }) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)', fontWeight: 'var(--vn-font-weight-medium)' }}>
          <Globe size={14} color="var(--vn-fg-muted)" />
          {row.original.domain}
          <a
            href={`http://${row.original.domain}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ display: 'inline-flex', color: 'var(--vn-fg-subtle)' }}
            aria-label="Open domain"
          >
            <ExternalLink size={13} />
          </a>
        </span>
      ),
    };
    const actionsCol: ColumnDef<CDNDomain, unknown> = {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => {
        const d = row.original;
        return (
          <div style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton variant="ghost" size="sm" aria-label="Domain actions">
                  <MoreHorizontal size={16} />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => navigate(detailHref(d.id))}>View details</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setMoveTarget(d)}>Move to service</DropdownMenuItem>
                <DropdownMenuItem tone="danger" disabled={!isDomainDeletable(d.status)} onSelect={() => setPendingDelete(d)}>
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    };
    return [domainCol, ...TOGGLEABLE.filter((c) => visible[c.id]).map((c) => all[c.id]), actionsCol];
  }, [navigate, visible]);

  return (
    <>
      <Container size="xl">
        <PageHeader
          icon={<Globe size={22} />}
          breadcrumbs={<CdnConsoleBreadcrumbs trail={[{ label: 'Domains' }]} />}
          title={`Domains${total ? ` (${total})` : ''}`}
          description="Manage CDN domain configurations. Click a row to view or edit."
          actions={
            <Button startIcon={<Plus size={15} />} onClick={() => navigate(addDomainPath)}>
              Add domain
            </Button>
          }
        />

        <Toolbar
          start={
            <Input
              placeholder="Find domains by name"
              startSlot={<Search />}
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
              style={{ maxWidth: 320 }}
            />
          }
          end={
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
              <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="secondary" startIcon={<Filter size={15} />}>
                    Filters{conditions.length ? ` (${conditions.length})` : ''}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" style={{ padding: 0 }}>
                  <FilterBuilder
                    fields={filterFields}
                    defaultValue={conditions.length ? conditions : undefined}
                    onApply={(c) => {
                      // Drop empty rows so a blank filter doesn't count as active.
                      setConditions(c.filter((x) => x.value.trim()));
                      setPage(1);
                      setFilterOpen(false);
                    }}
                    onClose={() => setFilterOpen(false)}
                  />
                </PopoverContent>
              </Popover>
              {conditions.length > 0 && (
                <Button variant="ghost" onClick={() => { setConditions([]); setPage(1); }}>
                  Clear
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                    Display options
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                  {TOGGLEABLE.map((c) => (
                    <DropdownMenuCheckboxItem
                      key={c.id}
                      checked={visible[c.id]}
                      onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {c.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Export scope: current page only (not all pages). Label is explicit so
                  users understand the scope — the selection bar handles selected-only export. */}
              <Button
                variant="secondary"
                startIcon={<Download size={15} />}
                disabled={rows.length === 0}
                onClick={() => exportCsv(rows, `cdn-domains-page-${page}.csv`)}
              >
                Export page
              </Button>
            </div>
          }
        />

        {/* Contextual selection bar — only visible when rows are checked.
            Kept separate from the Toolbar so bulk actions don't crowd the persistent controls. */}
        {selectedCount > 0 && (
          <div className={styles.selectionBar}>
            <span className={styles.selectionCount}>
              <strong>{selectedCount}</strong> selected
            </span>
            <div className={styles.selectionSpacer} />
            <Button variant="secondary" startIcon={<Tag size={15} />} onClick={() => setTagsOpen(true)}>
              Add tags
            </Button>
            <Button variant="secondary" startIcon={<FolderPlus size={15} />} onClick={() => setAssignOpen(true)}>
              Assign to project
            </Button>
            <Button
              variant="secondary"
              startIcon={<Download size={15} />}
              onClick={() => exportCsv(selectedRows, `cdn-domains-selected-${selectedCount}.csv`)}
            >
              Export selected ({selectedCount})
            </Button>
            <Button variant="ghost" startIcon={<X size={15} />} onClick={() => setRowSelection({})}>
              Clear selection
            </Button>
          </div>
        )}

        {isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 48 }} />
            ))}
          </div>
        ) : isError ? (
          <Alert
            tone="danger"
            title="Couldn't load domains"
            actions={<button onClick={() => refetch()}>Retry</button>}
          >
            The domains list could not be loaded. Check your permissions or try again.
          </Alert>
        ) : total === 0 && !debouncedSearch && conditions.length === 0 ? (
          <EmptyState
            icon={<Globe size={28} />}
            title="No domains yet"
            description="Add your first domain to start serving traffic from the edge."
            actions={
              <Button startIcon={<Plus size={15} />} onClick={() => navigate(addDomainPath)}>
                Add domain
              </Button>
            }
          />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={rows}
              getRowId={(r) => String(r.id)}
              enableSelection
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              onRowClick={(r) => navigate(detailHref(r.id))}
              emptyState={<EmptyState size="sm" icon={<Globe size={20} />} title="No results" description="No domains match your search or filters." />}
              footer={`Showing ${rows.length ? (page - 1) * PAGE_SIZE + 1 : 0}–${(page - 1) * PAGE_SIZE + rows.length} of ${total}`}
            />
            {pageCount > 1 && (
              <div style={{ marginTop: 'var(--vn-space-4)' }}>
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  onPageChange={setPage}
                  summary={isFetching ? 'Loading…' : undefined}
                />
              </div>
            )}
          </>
        )}

        <ConfirmDialog
          open={!!pendingDelete}
          onOpenChange={(o) => !o && setPendingDelete(null)}
          title="Delete CDN domain?"
          description={`This permanently removes all configuration for "${pendingDelete?.domain}". This action cannot be undone.`}
          confirmLabel="Delete"
          destructive
          requireText={pendingDelete?.domain}
          loading={remove.isPending}
          onConfirm={() => pendingDelete && remove.mutate(String(pendingDelete.id))}
        />

        <DomainAssignDialog
          open={assignOpen}
          onOpenChange={setAssignOpen}
          domains={selectedRows}
          onDone={() => setRowSelection({})}
        />

        <DomainTagsDialog
          open={tagsOpen}
          onOpenChange={setTagsOpen}
          domains={selectedRows}
          onDone={() => setRowSelection({})}
        />

        <DomainMoveDialog
          open={!!moveTarget}
          onOpenChange={(o) => !o && setMoveTarget(null)}
          domain={moveTarget}
          onDone={() => setMoveTarget(null)}
        />
      </Container>
    </>
  );
}
